import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    AlertTitle,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    Paper,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import {
    CheckCircle as CheckIcon,
    Info as InfoIcon,
    Search as SearchIcon,
    TrendingUp as UpgradeIcon,
    Tv as TvIcon
} from '@mui/icons-material';
import axios from 'axios';

const formatPrice = (price) => `$${Number(price || 0).toFixed(2)}/month`;

const pluralize = (count, singular, plural = `${singular}s`) => `${count} ${count === 1 ? singular : plural}`;

function buildSearchText(option) {
    return [
        option.name,
        option.type,
        ...(option.search_aliases || []),
        ...(option.packs || [])
    ].filter(Boolean).join(' ').toLowerCase();
}

function RecommendationCard({
    title,
    icon,
    tone = 'info',
    recommendation,
    actionLabel,
    onUse,
    selectionContext,
    selected = false,
    tvPriceAdjustment = null,
    tvGiftAdjustment = null,
    children
}) {
    if (!recommendation) {
        return null;
    }

    const borderColor = tone === 'success' ? 'success.main' : tone === 'secondary' ? 'secondary.main' : 'info.main';
    const bgColor = selected ? 'rgba(46, 125, 50, 0.10)' : 'background.paper';
    const total = recommendation.total ?? recommendation.price ?? recommendation.basePrice ?? 0;
    const adjustmentAmount = Number(tvPriceAdjustment?.amount || 0);
    const giftAdjustmentAmount = selected ? Number(tvGiftAdjustment?.amount || 0) : 0;
    const displayTotal = total + adjustmentAmount + giftAdjustmentAmount;

    return (
        <Card variant="outlined" sx={{ height: '100%', borderColor, bgcolor: bgColor }}>
            <CardContent>
                <Stack spacing={1.5}>
                    <Alert severity={tone === 'success' ? 'success' : 'info'} icon={icon}>
                        <AlertTitle sx={{ mb: 0 }}>{title}</AlertTitle>
                    </Alert>

                    <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap">
                        <Box>
                            <Typography variant="h6">{recommendation.pkgStr}</Typography>
                            {selected && (
                                <Chip label="Selected in bundle" color="success" size="small" sx={{ mt: 0.75 }} />
                            )}
                        </Box>
                        <Box textAlign={{ xs: 'left', sm: 'right' }}>
                            <Typography variant="h5" color="primary.main">
                                {formatPrice(displayTotal)}
                            </Typography>
                            {adjustmentAmount > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                    Includes {tvPriceAdjustment.label} +{formatPrice(adjustmentAmount).replace('/month', '')}
                                </Typography>
                            )}
                            {giftAdjustmentAmount > 0 && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Includes {tvGiftAdjustment.label} +{formatPrice(giftAdjustmentAmount).replace('/month', '')}
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    <List dense disablePadding>
                        {recommendation.basePrice !== undefined && (
                            <ListItem disableGutters>
                                <ListItemText
                                    primary="Base price"
                                    secondary={formatPrice(recommendation.basePrice)}
                                />
                            </ListItem>
                        )}
                        {recommendation.addOnCount > 0 && (
                            <ListItem disableGutters>
                                <ListItemText
                                    primary={`Add-on: ${pluralize(recommendation.addOnCount, 'extra theme pack')}`}
                                    secondary={`+ ${formatPrice(recommendation.addOnTotal)}`}
                                />
                            </ListItem>
                        )}
                        {adjustmentAmount > 0 && (
                            <ListItem disableGutters>
                                <ListItemText
                                    primary={tvPriceAdjustment.label}
                                    secondary={`+ ${formatPrice(adjustmentAmount)}`}
                                />
                            </ListItem>
                        )}
                        {giftAdjustmentAmount > 0 && (
                            <ListItem disableGutters>
                                <ListItemText
                                    primary={tvGiftAdjustment.label}
                                    secondary={`+ ${formatPrice(giftAdjustmentAmount)}`}
                                />
                            </ListItem>
                        )}
                    </List>

                    {recommendation.note && (
                        <Alert severity="info" variant="outlined">
                            {recommendation.note}
                        </Alert>
                    )}

                    {children}

                    {actionLabel && onUse && (
                        <Button variant="contained" color="success" startIcon={<CheckIcon />} onClick={() => onUse(recommendation, selectionContext)}>
                            {actionLabel}
                        </Button>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}

function BundleBuilderTvRecommender({
    onUseBestValue,
    selectedRecommendationKey,
    tvPriceAdjustment = null,
    tvGiftAdjustment = null
}) {
    const [channels, setChannels] = useState([]);
    const [selectedChannels, setSelectedChannels] = useState([]);
    const [recommendation, setRecommendation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [calculating, setCalculating] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            setLoading(true);
            setError('');

            try {
                const [channelsRes] = await Promise.all([
                    axios.get('/api/tv-packages-v2/channels'),
                    axios.get('/api/tv-packages-v2/pricing')
                ]);

                if (isMounted) {
                    setChannels(channelsRes.data.channels || []);
                }
            } catch (fetchError) {
                if (isMounted) {
                    setError('Failed to load TV channel data.');
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
    }, []);

    const recommendationKey = useMemo(() => {
        const bestValue = recommendation?.bestValue;
        if (!bestValue) {
            return '';
        }

        return `${bestValue.key || bestValue.pkgStr}-${bestValue.total ?? bestValue.price ?? bestValue.basePrice ?? 0}-${bestValue.addOnCount || 0}`;
    }, [recommendation]);

    const handleSelectionChange = (event, newValue) => {
        const lastAdded = newValue[newValue.length - 1];

        if (lastAdded) {
            const duplicate = selectedChannels.some((item) => item.name === lastAdded.name && item.type === lastAdded.type);
            if (duplicate && newValue.length > selectedChannels.length) {
                return;
            }

            if (lastAdded.type === 'theme-pack') {
                const conflictingChannels = selectedChannels.filter(
                    (channel) => {
                        const channelKeys = channel.channels || [channel.name];
                        return channel.type === 'channel'
                            && lastAdded.channels?.some((packChannel) =>
                                channelKeys.some((channelKey) => channelKey.toLowerCase() === packChannel.toLowerCase())
                                || packChannel.toLowerCase() === channel.name.toLowerCase()
                            );
                    }
                );

                if (conflictingChannels.length > 0) {
                    setError(`Cannot add this theme pack. Individual channels are already selected: ${conflictingChannels.map((channel) => channel.name).join(', ')}`);
                    return;
                }
            }

            if (lastAdded.type === 'channel') {
                const lastAddedKeys = lastAdded.channels || [lastAdded.name];
                const conflictingPacks = selectedChannels.filter(
                    (channel) => channel.type === 'theme-pack'
                        && channel.channels?.some((packChannel) =>
                            lastAddedKeys.some((channelKey) => channelKey.toLowerCase() === packChannel.toLowerCase())
                            || packChannel.toLowerCase() === lastAdded.name.toLowerCase()
                        )
                );

                if (conflictingPacks.length > 0) {
                    setError(`This channel is already included in the selected theme pack: ${conflictingPacks[0].name}`);
                    return;
                }
            }
        }

        setSelectedChannels(newValue);
        setRecommendation(null);
        setError('');
    };

    const handleGetRecommendation = async () => {
        if (selectedChannels.length === 0) {
            return;
        }

        setCalculating(true);
        setError('');

        try {
            const response = await axios.post('/api/tv-packages-v2/recommend', {
                selectedChannels
            });
            setRecommendation(response.data.recommendation);
        } catch (recommendationError) {
            setError('Failed to calculate TV recommendation.');
        } finally {
            setCalculating(false);
        }
    };

    return (
        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 2, borderColor: 'primary.light' }}>
            <Stack spacing={2}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <TvIcon color="primary" />
                            <Typography variant="h6">TV Package Recommender</Typography>
                            <Chip label="V2 live pricing" size="small" color="primary" variant="outlined" />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                            Uses the existing TV Package Calculator V2 APIs for channel matching, add-ons, and best-value logic.
                        </Typography>
                    </Box>
                    <Chip label="Alias search should be verified after integration" size="small" variant="outlined" />
                </Box>

                {error && (
                    <Alert severity="warning" onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                <Autocomplete
                    multiple
                    loading={loading}
                    options={channels}
                    value={selectedChannels}
                    onChange={handleSelectionChange}
                    isOptionEqualToValue={(option, value) => option.name === value.name && option.type === value.type}
                    filterOptions={(options, state) => {
                        const query = state.inputValue.trim().toLowerCase();
                        if (!query) {
                            return options;
                        }

                        return options.filter((option) => buildSearchText(option).includes(query));
                    }}
                    getOptionLabel={(option) => option.name}
                    groupBy={(option) => {
                        if (option.type === 'theme-pack') return 'Theme Packs';
                        if (option.type === 'premium') return 'Premium Channels';
                        if (option.type === 'core') return 'Core Channels';
                        return 'Theme Pack Channels';
                    }}
                    renderOption={(props, option) => (
                        <Box component="li" {...props}>
                            <Box sx={{ width: '100%' }}>
                                <Typography variant="body2">{option.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {option.type === 'theme-pack' ? 'Theme Pack' : option.type}
                                    {option.packs && option.packs.length > 0 && ` | ${option.packs.join(', ')}`}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                variant="outlined"
                                label={option.name}
                                color={
                                    option.type === 'premium' ? 'secondary'
                                        : option.type === 'theme-pack' ? 'primary'
                                            : 'default'
                                }
                                size="small"
                                {...getTagProps({ index })}
                            />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Search for channels or theme packs..."
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <>
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                        {params.InputProps.startAdornment}
                                    </>
                                )
                            }}
                        />
                    )}
                />

                <Button
                    variant="contained"
                    onClick={handleGetRecommendation}
                    disabled={selectedChannels.length === 0 || loading || calculating}
                    startIcon={calculating ? <CircularProgress size={18} /> : <CheckIcon />}
                >
                    {calculating ? 'Calculating...' : 'Get Recommendation'}
                </Button>

                {recommendation && (
                    <Stack spacing={2}>
                        {(recommendation.requiredThemePacks?.length > 0 || recommendation.premiumChannels?.length > 0) && (
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="subtitle2" gutterBottom>Selection Summary</Typography>
                                <Grid container spacing={1.5}>
                                    {recommendation.requiredThemePacks?.length > 0 && (
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="caption" color="text.secondary">
                                                Required Theme Packs ({recommendation.requiredThemePacks.length})
                                            </Typography>
                                            <Stack direction="row" flexWrap="wrap" gap={0.75} useFlexGap sx={{ mt: 0.75 }}>
                                                {recommendation.requiredThemePacks.map((pack) => (
                                                    <Chip key={pack} label={pack} size="small" color="primary" />
                                                ))}
                                            </Stack>
                                        </Grid>
                                    )}
                                    {recommendation.premiumChannels?.length > 0 && (
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="caption" color="text.secondary">
                                                Premium Channels ({recommendation.premiumChannels.length})
                                            </Typography>
                                            <Stack direction="row" flexWrap="wrap" gap={0.75} useFlexGap sx={{ mt: 0.75 }}>
                                                {recommendation.premiumChannels.map((channel) => (
                                                    <Chip key={channel} label={channel} size="small" color="secondary" />
                                                ))}
                                            </Stack>
                                        </Grid>
                                    )}
                                </Grid>
                            </Paper>
                        )}

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={recommendation.alternative ? 6 : 12}>
                                <RecommendationCard
                                    title="Best Value Package"
                                    icon={<CheckIcon />}
                                    tone="success"
                                    recommendation={recommendation.bestValue}
                                    actionLabel="Use Best Value in Bundle"
                                    onUse={onUseBestValue}
                                    selectionContext={{ selectedChannels, recommendation }}
                                    selected={selectedRecommendationKey && selectedRecommendationKey === recommendationKey}
                                    tvPriceAdjustment={tvPriceAdjustment}
                                    tvGiftAdjustment={tvGiftAdjustment}
                                />
                            </Grid>
                            {recommendation.alternative && (
                                <Grid item xs={12} md={6}>
                                    <RecommendationCard
                                        title="Alternative Package"
                                        icon={<InfoIcon />}
                                        recommendation={recommendation.alternative}
                                        tvPriceAdjustment={tvPriceAdjustment}
                                    />
                                </Grid>
                            )}
                        </Grid>

                        {recommendation.upsell && recommendation.upsell.priceDiff > 0 && (
                            <Card variant="outlined" sx={{ borderColor: 'secondary.main', bgcolor: 'rgba(103, 58, 183, 0.06)' }}>
                                <CardContent>
                                    <Stack spacing={1.5}>
                                        <Alert severity="info" icon={<UpgradeIcon />}>
                                            <AlertTitle>Upgrade Opportunity</AlertTitle>
                                            For just <strong>{formatPrice(recommendation.upsell.priceDiff)}</strong> more, the customer gets more included value.
                                        </Alert>
                                        <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap">
                                            <Typography variant="h6">{recommendation.upsell.pkgStr}</Typography>
                                            <Typography variant="h5" color="secondary.main">
                                                {formatPrice(recommendation.upsell.price)}
                                            </Typography>
                                        </Box>
                                        <Stack direction="row" flexWrap="wrap" gap={0.75} useFlexGap>
                                            {recommendation.upsell.extraThemes > 0 && (
                                                <Chip label={pluralize(recommendation.upsell.extraThemes, 'extra theme pack')} color="primary" size="small" />
                                            )}
                                            {recommendation.upsell.extraPremiums > 0 && (
                                                <Chip label={pluralize(recommendation.upsell.extraPremiums, 'extra premium channel')} color="secondary" size="small" />
                                            )}
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                        )}
                    </Stack>
                )}
            </Stack>
        </Paper>
    );
}

export default BundleBuilderTvRecommender;
